import * as React from 'react'
import * as Api from 'src/api'
import { type LanguageString } from 'src/api/types'
import { useApi, type APIResponse } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { type Option } from 'src/views/components/forms/formik/SelectInput'
import { courseToFormValues } from './mapModels'
import { type CourseEditFormValues } from './types'

export interface CourseEditPageData {
  readonly course: Api.AdministratorCourse | null | undefined
  readonly faculties: Api.getRegimeFacultiesOk | null | undefined
  readonly degrees: readonly Api.AdministratorListsType[] | null | undefined
  readonly courseDefinitions: Api.AdministratorCourseDefinition | null | undefined
  readonly courseTypeOptions: readonly Option[]
  readonly creditTypes: readonly Api.AdministratorListsType[] | null | undefined
  readonly initialValues: CourseEditFormValues
  readonly isLoading: boolean
  readonly mutateCourse: () => Promise<Api.AdministratorCourse | null | undefined>
}

export function useCourseEditPageData({
  courseId,
  headers,
  semId,
}: {
  readonly courseId: string | undefined
  readonly headers: AuthenticatedHeaders
  readonly semId: string
  readonly locale: LanguageString
}): CourseEditPageData {
  const {
    data: course,
    isLoading: isCourseLoading,
    mutate: mutateCourse,
  }: APIResponse<Api.AdministratorCourse | null, Error> = useApi({
    endpoint: Api.getAdministratorCourse,
    params: React.useMemo(
      () => ({
        headers,
        query: { semId: parseInt(semId, 10) },
        args: { id: courseId! },
      }),
      [courseId, headers, semId]
    ),
    shouldFetch: courseId != null && semId.length > 0,
    suspense: false,
  })

  const { data: degreesResponse, isLoading: isDegreesLoading } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const degrees = degreesResponse
  const { data: faculties, isLoading: isFacultiesLoading } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: courseDefinitions, isLoading: isDefinitionsLoading } = useApi({
    endpoint: Api.getAdministratorCourseDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: courseTypesList, isLoading: isCourseTypesLoading } = useApi({
    endpoint: Api.getAdministratorListsCourseTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: creditTypesResponse, isLoading: isCreditTypesLoading } = useApi({
    endpoint: Api.getAdministratorListsCreditTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const creditTypes = creditTypesResponse?.data

  const courseTypeOptions = React.useMemo(
    () => (courseTypesList ?? []).map((courseType) => ({ value: courseType.id, label: courseType.name })),
    [courseTypesList]
  )

  const initialValues = React.useMemo(() => courseToFormValues(course), [course])

  const isLoading =
    isCourseLoading ||
    isFacultiesLoading ||
    isDegreesLoading ||
    isDefinitionsLoading ||
    isCourseTypesLoading ||
    isCreditTypesLoading

  return {
    course,
    faculties,
    degrees,
    courseDefinitions,
    courseTypeOptions,
    creditTypes,
    initialValues,
    isLoading,
    mutateCourse,
  }
}
